import React, { useEffect, useState, useContext, useRef, useImperativeHandle } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import api from "services/api";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";

import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";

import SuiBadge from "components/SuiBadge";
import SuiTypography from "components/SuiTypography";
import SuiBadgeDot from "components/SuiBadgeDot";
import SuiBox from "components/SuiBox";
import SuiAvatar from "components/SuiAvatar";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageIcon from "@mui/icons-material/Image";

import { capitalCase } from "capital-case";
import { AccountContext } from "context/account";
import CategoryContext from "context/category-context";

import toast from "react-hot-toast";
import moment from "moment";

import { filterParser, sortParser, compare } from "utils/helper";
import { stylesSortOptions, seasonOptions } from "utils/data";

import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import Prompt from "components/UpStyled/Prompt";

import { collectionSortOptions } from "utils/data";

const SetMoodboardCollectionModal = React.forwardRef(
  (
    { visible, handleClose, handleOk, moodboard, lockCollection, handleRefresh = () => {} },
    ref
  ) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [selectedCollection, setSelectedCollection] = useState({});
    const [collectionList, setCollectionList] = useState([]);
    const [filteredCollectionList, setFilteredCollectionList] = useState([]);
    const [totalNumber, setTotalNumber] = useState(0);
    const [orderOpt, setOrderOpt] = useState(collectionSortOptions[4]);
    const [searchStr, setSearchStr] = useState("");
    const [confirmVisible, setConfirmVisible] = useState(false);

    const navigate = useNavigate();

    const existedMoodboard = selectedCollection?.moodboard?.[0];

    const account = useContext(AccountContext);

    useImperativeHandle(
      ref,
      () => ({
        selectedCollection,
        setSelectedCollection,
      }),
      [selectedCollection]
    );

    useEffect(() => {
      if (visible) {
        retrieveCollections();
      }
    }, [visible]);

    const retrieveCollections = async (sort = "") => {
      try {
        const { data } = await api.collections.list({ order: "-updated_at" });

        let collections = data["collections"] ? data["collections"] : [];

        collections = collections.map((x) => ({
          ...x,
          season: x?.metadata?.season?.value || "",
          year: x?.metadata?.year || "",
        }));
        setCollectionList(collections);
        setTotalNumber(collections.length);

        //filter based on pagination
        const sliceStart = pageSize * (page - 1);
        const sliceEnd =
          pageSize * page > collections.length ? collections.length : pageSize * page;
        const newFilteredList = collections.slice(sliceStart, sliceEnd);

        setFilteredCollectionList(newFilteredList);
      } catch (err) {
        toast.error(`Retrieve collection list failed`);
        console.log(err);
      }
    };

    const handleFilterCollections = (page, pageSize, str = searchStr) => {
      const copyProjects = [...collectionList];
      let filteredList = copyProjects; //all projects

      if (str) {
        const strUpper = str.toUpperCase();
        const searchFields = ["title", "description"];
        filteredList = filteredList.filter((x) => {
          let result = false;
          for (let i = 0; i < searchFields.length; i++) {
            if (x[searchFields[i]]) {
              if (typeof x[searchFields[i]] == "string") {
                if (x[searchFields[i]].toUpperCase().includes(strUpper)) {
                  result = true;
                }
              } else if (typeof x[searchFields[i]] == "object") {
                const text = JSON.stringify(x[searchFields[i]]);
                if (text.toUpperCase().includes(strUpper)) {
                  result = true;
                }
              }
            }
          }
          return result;
        });
      }

      setTotalNumber(filteredList.length);

      let newPage = page;
      //filter based on pagination
      const sliceStart = pageSize * (newPage - 1);
      const sliceEnd = pageSize * newPage > totalNumber ? totalNumber : pageSize * newPage;
      const newFilteredList = filteredList.slice(sliceStart, sliceEnd);

      setFilteredCollectionList(newFilteredList);
    };

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
      handleFilterCollections(newPage, pageSize, searchStr);
    };

    const handleChangeRowsPerPage = (option) => {
      const newPageSize = parseInt(option.value, 10);
      setPageSize(newPageSize);
      setPage(1);
      handleFilterCollections(1, newPageSize, searchStr);
    };

    const pageCount = parseInt(Math.ceil(totalNumber / pageSize), 10);
    const pageOptions = [];
    for (let i = 0; i < pageCount; i++) {
      pageOptions.push(i);
    }
    const entriesStart = pageSize * (page - 1) + 1;
    const entriesEnd = pageSize * page > totalNumber ? totalNumber : pageSize * page;

    const handleSearchChange = (str) => {
      let formattedStr = "";
      if (str) {
        formattedStr = str.trim();
      }
      setSearchStr(formattedStr);
      handleFilterCollections(page, pageSize, formattedStr);
    };

    const handleOnSortChange = (e) => {
      setOrderOpt(e);
      let sortStr = e?.value;
      const newFilteredCollectionList = filteredCollectionList.sort((a, b) =>
        compare(a, b, sortStr)
      );
      setFilteredCollectionList(newFilteredCollectionList);
    };

    const handleSetMoodboard = async (collection) => {
      //associate the moodboard with created collection
      if (moodboard?.id && collection?.id) {
        try {
          await api.collections.update(collection.id, {
            moodboard: [{ id: moodboard.id }],
          });

          toast.success(`Set moodboard to collection successfully!`);
          //navigate to collection detail page
          navigate(`/workspace/collections?id=${collection.id}`);
        } catch (err) {
          toast.error(`Set moodboard to collection failed!`);
        }
      } else {
        toast.error(`Can't find the moodboard!`);
      }
    };

    return (
      <Dialog
        open={visible}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xl"
      >
        <DialogTitle id="alert-dialog-title">
          Choose your collection
          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <SuiBox>
            <SuiBox display="flex" alignItems="center" justifyContent="space-between" mb={2} mr={1}>
              <SuiBox mt={1} ml={0.5} display="flex" itemsAlign="center" gap={1}>
                <ImageIcon fontSize="small" color="text" />
                <SuiTypography component="label" variant="body1" textTransform="none" color="text">
                  Set the current moodboard to selected collection.
                </SuiTypography>{" "}
              </SuiBox>

              <SuiBox display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                <SuiBox>
                  <Card sx={{ borderRadius: 2, width: 200 }}>
                    <SuiSelect
                      options={collectionSortOptions}
                      placeholder="Sort"
                      isClearable
                      onChange={handleOnSortChange}
                      value={orderOpt}
                    />
                  </Card>
                </SuiBox>
                <SuiInput
                  placeholder="Search Keywords"
                  type="text"
                  onChange={(e) => handleSearchChange(e?.target?.value)}
                  icon={{
                    direction: "right",
                    component: (
                      <SearchIcon
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleSearchChange(searchStr)}
                      />
                    ),
                  }}
                />
              </SuiBox>
            </SuiBox>
            <SuiBox>
              <SuiBox>
                <SuiTypography variant="h6">Collections</SuiTypography>
                <SuiBox sx={{ maxHeight: 500, overflow: "auto" }}>
                  <SuiBox
                    display="flex"
                    justifyContent="flex-start"
                    flexDirection={"row"}
                    sx={{ overflowY: "auto", flexWrap: "wrap" }}
                    gap={1}
                  >
                    {filteredCollectionList.map((col, index) => {
                      let moodboardImg = col?.moodboard?.[0]?.img;
                      return (
                        <SuiBox
                          key={index}
                          onClick={() => {
                            setSelectedCollection(col);
                          }}
                          sx={{
                            border: ({ borders }) =>
                              selectedCollection.id == col.id
                                ? "2px solid rgb(34 211 238)"
                                : `2px solid ${borders.borderColor}`,
                            boxShadow: ({ boxShadows: { md } }) =>
                              selectedCollection.id == col.id ? md : "none",
                            cursor: "pointer",
                          }}
                        >
                          <ImageCardWrapper
                            data={moodboardImg}
                            mode={"normal"}
                            height={200}
                            width={240}
                            iconMode
                          />
                          <Tooltip title={col.title}>
                            <SuiTypography
                              variant="h6"
                              sx={{
                                pt: 0.5,
                                pl: 0.5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: "1",
                                WebkitBoxOrient: "vertical",
                                maxWidth: 230,
                              }}
                            >
                              {col.title}
                            </SuiTypography>
                          </Tooltip>
                        </SuiBox>
                      );
                    })}
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </SuiBox>

            <SuiBox
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              p={3}
            >
              <SuiBox display="flex" alignItems="center">
                <SuiBox mb={{ xs: 3, sm: 0 }}>
                  <SuiTypography variant="button" color="secondary" fontWeight="regular">
                    Showing {entriesStart} to {entriesEnd} of {totalNumber} entries
                  </SuiTypography>
                </SuiBox>

                <SuiBox display="flex" alignItems="center" gap={1}>
                  <SuiSelect
                    defaultValue={{ value: pageSize, label: pageSize }}
                    options={[10, 20, 50].map((entry) => ({ value: entry, label: entry }))}
                    onChange={handleChangeRowsPerPage}
                    size="small"
                  />

                  <SuiTypography variant="caption" color="secondary">
                    entries per page
                  </SuiTypography>
                </SuiBox>
              </SuiBox>
              <Pagination
                variant={"outlined"}
                color={"info"}
                count={pageOptions.length}
                defaultPage={page}
                onChange={handleChangePage}
              />
            </SuiBox>
            {confirmVisible ? (
              <Prompt
                heading={`Set Moodboard `}
                text={`The collection already have a moodboard. Do you still want to apply the current moodboard to your collection?`}
                visible={confirmVisible}
                okText={"Confirm"}
                okColor="dark"
                handleOk={async () => {
                  handleSetMoodboard(selectedCollection);
                }}
                handleClose={() => setConfirmVisible(false)}
              />
            ) : null}
          </SuiBox>
        </DialogContent>
        <DialogActions>
          <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
            Cancel
          </SuiButton>
          <SuiButton
            size="medium"
            variant="gradient"
            color="dark"
            onClick={() =>
              handleOk
                ? handleOk(selectedCollection)
                : existedMoodboard?.id
                ? setConfirmVisible(true)
                : handleSetMoodboard(selectedCollection)
            }
          >
            {existedMoodboard?.id ? "Update" : "Set"}
          </SuiButton>
        </DialogActions>
      </Dialog>
    );
  }
);

export default SetMoodboardCollectionModal;
